import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest
} from '@angular/common/http';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Router } from '@angular/router';

import { Store } from '@ngrx/store';
import { EMPTY, Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { IAppState } from '../../../ngrx';
import { environment } from '../../../../environments/environment';
import { AuthActions } from '../../../auth';
import { AuthService } from '../../../auth/services/auth.service';


@Injectable()
export class CoreHttpInterceptor implements HttpInterceptor {
  constructor(
        private store: Store<IAppState>,
        private authService: AuthService,
        private router: Router,
        @Inject(PLATFORM_ID) private platformId
    ) {
    }


  /**
   * Intercepts HTTP requests to add authentication headers and handle token expiration.
   * @param req The HTTP request object.
   * @param next The HTTP handler for the next interceptor.
   * @returns An observable representing the HTTP response.
   */
  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<any> {
    // should not intercept requests that are not to the API
    const apiUrls = [environment.apiUrl, environment.statisticUrl, environment.aiUrl];

    if (!apiUrls.some(url => req.url.includes(url))) {
      return next.handle(req);
    }

    let authReq = this.addHeaders(req); // add content-type and accept

    if (!isPlatformBrowser(this.platformId) && this.authService.isMixedRequest(req.url)) {
      return next.handle(authReq);
    }

    const token = {
      access: this.authService.getTokenFromStorage(),
      refresh: this.authService.getRefreshTokenFromStorage()
    };

    // if token is required - add it to headers
    if (token.access && this.authService.shouldAddToken(req.url)) {
      authReq = this.addHeaders(req, token);
    }

    return next.handle(authReq).pipe(
      catchError((error: any) => {
        if (error instanceof HttpErrorResponse && error.status === 401 && this.authService.shouldRefreshToken(req.url)) {
          if (!isPlatformBrowser(this.platformId)) {
            return EMPTY;
          }

          return this.authService.tryToRefreshToken(
            (newToken, innerNext, innerReq) => {
              return this.retryRequestWithToken(newToken, innerNext, innerReq);
            }, // retry request with new token if refresh is successful

            this.authService.isMixedRequest(req.url)
              // if refresh fails and endpoint can be public, retry request as public without token
              ? (error, innerNext, innerReq) => this.retryRequestAsPublic(innerNext, innerReq)
              // clear auth data if not a mixed request
              : () => {
                this.store.dispatch(AuthActions.logout({
                  redirectToLogin: true
                }));
                return throwError(() => error);
              },
            next, req);
        }

        return throwError(() => error);
      })
    ) as Observable<HttpEvent<any>>;
  }

  retryRequestWithToken(token: {access: string, refresh: string}, next, request): Observable<HttpEvent<any>> {
    return next.handle(this.addHeaders(request, token)).pipe(
      catchError((error) => {
        if (error instanceof HttpErrorResponse && error.status === 401 && this.authService.shouldRefreshToken(request.url)) {
          // Refreshed token still got 401 — now logout
          this.store.dispatch(AuthActions.logout({
            redirectToLogin: true
          }));
          return throwError(() => error);
        }

        return throwError(() => error);
      })
    );
  }

  retryRequestAsPublic(next, request): Observable<HttpEvent<any>> {
    this.store.dispatch(AuthActions.clearAuthData());

    return next.handle(this.addHeaders(request));
  }

  private addHeaders(req: HttpRequest<any>, token?: {access?: string, refresh?: string}): HttpRequest<any> {
    let headers = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    };

    if (token?.access) {
      headers = this.handleAuthToken(req.url, headers, token.access);
    }

    // logout and refresh token requests have refresh token in the body
    // so body should be updated with new refresh token
    if ((req.url.includes('logout') || req.url.includes('api-token-refresh')) && token?.refresh) {
      return req.clone({
        setHeaders: headers,
        body: { ...req.body, refresh: token?.refresh }
      });
    }

    return req.clone({
      setHeaders: headers
    });
  }

  private handleAuthToken(url: string, headers, token = null): any {
    let prefix = '';

    if (url.match(environment.apiUrl) || url.match(environment.aiUrl)) {
      prefix = 'JWT ';
    }

    if (url.match(environment.statisticUrl)) {
      prefix = 'Bearer ';
    }

    if (token) {
      headers.Authorization = `${prefix + token}`;
    }

    return headers;
  }

}

